import React, { useEffect, useState } from "react";
import { api, useLang, usePerm } from "../lib/providers";
import { PageHeader, Btn, Field, TextInput, TextArea, EmptyState } from "../components/primitives";
import { useConfirm } from "../components/ConfirmDialog";
import PresenceBar from "../components/PresenceBar";
import usePresence from "../hooks/usePresence";
import { Plus, PencilSimple, Trash, MagnifyingGlass, FilePdf } from "@phosphor-icons/react";
import { downloadCustomerBundle } from "../lib/bundle";

const empty = { name: "", company: "", email: "", phone: "", address: "", tax_id: "", notes: "" };

export default function CustomersPage() {
  const { t, lang } = useLang();
  const confirm = useConfirm();
  const { can, level } = usePerm();
  const canCreate = can("customers", "create");
  const canEdit = can("customers", "edit");
  const canDelete = can("customers", "delete");
  const notesOnly = level("customers") === "view_notes_only";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const { others } = usePresence({ resource: "customers", enabled: true });
  const editingId = editing?.id || null;
  const { holder: editHolder } = usePresence({ resource: "customers", itemId: editingId, edit: !!editingId, enabled: !!editingId });

  const load = (search = "") => {
    setLoading(true);
    api.get(`/customers${search ? `?q=${encodeURIComponent(search)}` : ""}`)
      .then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const tid = setTimeout(() => load(q), 250);
    return () => clearTimeout(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...editing };
    delete payload.id; delete payload.created_at;
    if (editing.id) await api.put(`/customers/${editing.id}`, payload);
    else await api.post("/customers", payload);
    setEditing(null);
    load();
  };

  const del = async (c) => {
    const ok = await confirm({
      title: `${t("confirmDeleteTitle")} ${c.name}`,
      description: t("confirmHardDeleteDesc"),
      confirmText: t("deleteButton"),
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/customers/${c.id}`);
    load();
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1300px] mx-auto">
      <PageHeader
        eyebrow="05 / CRM"
        title={t("customers")}
        description={lang === "de" ? "Kundenstamm für deine Angebote." : "Your customer base for quotes."}
        actions={canCreate ? (
          <div className="flex items-center gap-3">
            <PresenceBar others={others} lang={lang} />
            <Btn data-testid="new-customer-btn" onClick={() => setEditing({ ...empty })}><Plus size={16} /> {t("newCustomer")}</Btn>
          </div>
        ) : <PresenceBar others={others} lang={lang} />}
      />

      <div className="mb-4 max-w-sm relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-2)]" />
        <TextInput data-testid="customers-search" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
      </div>

      {editing && (
        <form onSubmit={save} className="swiss-card p-6 mb-6" data-testid="customer-form">
          {editHolder && (
            <div className="-m-6 mb-4 px-5 py-3 bg-[#F59E0B]/10 border-b border-[#F59E0B]/30 text-sm text-[#92400E] flex items-center gap-2" data-testid="customer-lock-warning">
              <span className="inline-block w-2 h-2 rounded-full bg-[#F59E0B]" />
              {lang === "de"
                ? <>Achtung: <b>{editHolder.user_name || editHolder.user_email}</b> bearbeitet diesen Kunden gerade.</>
                : <>Warning: <b>{editHolder.user_name || editHolder.user_email}</b> is currently editing this customer.</>}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={t("company")}>
              <TextInput required readOnly={notesOnly} data-testid="customer-company" value={editing.company} onChange={(e) => setEditing({ ...editing, company: e.target.value })} />
            </Field>
            <Field label={lang === "de" ? "Projektname" : "Project name"} hint={lang === "de" ? "Optional" : "Optional"}>
              <TextInput readOnly={notesOnly} data-testid="customer-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </Field>
            <Field label={t("email")}>
              <TextInput readOnly={notesOnly} type="email" data-testid="customer-email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
            </Field>
            <Field label={t("phone")}>
              <TextInput readOnly={notesOnly} data-testid="customer-phone" value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} />
            </Field>
            <Field label={t("address")} className="md:col-span-2">
              <TextArea readOnly={notesOnly} data-testid="customer-address" value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} />
            </Field>
            <Field label={t("taxId")}>
              <TextInput readOnly={notesOnly} data-testid="customer-tax-id" value={editing.tax_id} onChange={(e) => setEditing({ ...editing, tax_id: e.target.value })} />
            </Field>
            <Field label={t("notes")} className="md:col-span-2">
              <TextArea data-testid="customer-notes" value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
            </Field>
          </div>
          <div className="flex gap-2 mt-6">
            <Btn type="submit" data-testid="customer-save">{t("save")}</Btn>
            <Btn type="button" variant="ghost" onClick={() => setEditing(null)} data-testid="customer-cancel">{t("cancel")}</Btn>
          </div>
        </form>
      )}

      {loading ? (
        <div className="swiss-card p-8 text-[var(--muted)]">…</div>
      ) : items.length === 0 && !q ? (
        <EmptyState title={t("noCustomers")} action={canCreate ? <Btn onClick={() => setEditing({ ...empty })} data-testid="empty-new-customer"><Plus size={16} /> {t("newCustomer")}</Btn> : null} />
      ) : (
        <div className="swiss-card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[var(--bg)] text-[var(--muted)]">
              <tr>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("company")}</th>
                <th className="text-left font-medium px-5 py-3 label-overline">{lang === "de" ? "Projektname" : "Project name"}</th>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("email")}</th>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("phone")}</th>
                {(canEdit || canDelete) && <th className="text-right font-medium px-5 py-3 label-overline">{t("actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-[var(--border)] hover:bg-[var(--row-hover)] transition-colors fade-up">
                  <td className="px-5 py-3 font-medium">{c.company || "—"}</td>
                  <td className="px-5 py-3 text-[var(--muted)]">{c.name || "—"}</td>
                  <td className="px-5 py-3 text-[var(--muted)]">{c.email || "—"}</td>
                  <td className="px-5 py-3 text-[var(--muted)]">{c.phone || "—"}</td>
                  {(canEdit || canDelete) && (
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Btn
                          variant="ghost"
                          onClick={() => downloadCustomerBundle(c.id, c.company || c.name, lang)}
                          data-testid={`bundle-customer-${c.id}`}
                          className="h-8 px-2"
                          title={lang === "de" ? "Projektmappe als PDF" : "Project bundle as PDF"}
                        >
                          <FilePdf size={14} />
                        </Btn>
                        {canEdit && <Btn variant="ghost" onClick={() => setEditing(c)} data-testid={`edit-customer-${c.id}`} className="h-8 px-2"><PencilSimple size={14} /></Btn>}
                        {canDelete && <Btn variant="ghost" onClick={() => del(c)} data-testid={`delete-customer-${c.id}`} className="h-8 px-2 text-[#FF3B30] hover:bg-[#FF3B30]/5"><Trash size={14} /></Btn>}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
